import React from "react";
import { Link } from "react-router-dom";

const LAST_UPDATED = "April 4, 2026";
const COMPANY = "VaWeNa, LLC.";
const EMAIL = "legal@vawena.ai";
const APP_URL = "https://app.vawena.ai";

const prose = "text-gray-700 dark:text-slate-300";
const proseMuted = "text-gray-600 dark:text-slate-400";
const heading = "text-gray-950 dark:text-white";
const emphasis = "font-semibold text-gray-900 dark:text-slate-100";

const tocItems: [string, string][] = [
  ["1. Acceptance of Terms", "#acceptance"],
  ["2. Eligibility", "#eligibility"],
  ["3. Account Registration", "#account"],
  ["4. Subscription & Billing", "#billing"],
  ["5. Free Trial", "#trial"],
  ["6. Acceptable Use", "#use"],
  ["7. Financial Information Disclaimer", "#disclaimer"],
  ["8. Third-Party Services", "#third-party"],
  ["9. Intellectual Property", "#ip"],
  ["10. Data & Privacy", "#data"],
  ["11. Termination", "#termination"],
  ["12. Limitation of Liability", "#liability"],
  ["13. Dispute Resolution", "#disputes"],
  ["14. Changes to Terms", "#changes"],
  ["15. Contact", "#contact"],
];

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-28 border-t border-dashed border-stone-300/90 pt-10 first:border-t-0 first:pt-0 dark:border-white/15">
      <h2
        className={`mb-4 text-lg font-medium tracking-tight ${heading} underline decoration-indigo-400/40 decoration-1 underline-offset-[7px] dark:decoration-indigo-500/40`}
      >
        {title}
      </h2>
      <div className={`${prose} space-y-3 text-[15px] leading-relaxed sm:text-base`}>{children}</div>
    </section>
  );
}

function KeyPoint({
  children,
  accent = "neutral",
}: {
  children: React.ReactNode;
  accent?: "amber" | "rose" | "neutral";
}) {
  const border =
    accent === "amber"
      ? "border-amber-600/60 dark:border-amber-400/55"
      : accent === "rose"
        ? "border-rose-600/60 dark:border-rose-400/55"
        : "border-stone-400 dark:border-white/25";
  const bg =
    accent === "amber"
      ? "bg-amber-50/90 dark:bg-amber-500/[0.07]"
      : accent === "rose"
        ? "bg-rose-50/90 dark:bg-rose-500/[0.07]"
        : "bg-stone-100/80 dark:bg-white/[0.03]";
  return (
    <div className={`mb-4 border-l-4 ${border} ${bg} py-3 pl-4 pr-4`}>{children}</div>
  );
}

const linkIndigo =
  "text-indigo-800 underline-offset-2 hover:text-indigo-950 hover:underline dark:text-indigo-400 dark:hover:text-indigo-300";

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50/90 via-[#f0f4ff] to-[#e8eefc] dark:from-vw-bg dark:via-vw-bg dark:to-vw-bg">
      {/* Compact column — same max width as Privacy; layout is left-aligned “ledger”, not centered marketing hero */}
      <div className="mx-auto max-w-[720px] px-4 pb-24 pt-12 sm:px-6">
        <header className="mb-10 border-b-4 border-double border-stone-400/50 pb-8 dark:border-white/20">
          <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500 dark:text-slate-500">
            Legal document · Terms
          </p>
          <h1 className={`text-3xl font-semibold tracking-tight sm:text-4xl ${heading}`}>Terms of Service</h1>
          <p className={`mt-3 max-w-[42ch] text-sm leading-relaxed ${proseMuted}`}>
            Agreement between you and {COMPANY}. Effective and last reviewed{" "}
            <time className="tabular-nums" dateTime="2026-04-04">
              {LAST_UPDATED}
            </time>
            .
          </p>
        </header>

        <p className="mb-10 border-l-2 border-indigo-600 pl-4 text-[15px] leading-relaxed text-gray-700 dark:border-indigo-500 dark:text-slate-300 sm:text-base">
          These Terms of Service (&quot;<strong className={emphasis}>Terms</strong>&quot;) govern your access to and use of the VaWeNa platform operated by{" "}
          <strong className={emphasis}>{COMPANY}</strong> (&quot;<strong className={emphasis}>VaWeNa</strong>,&quot; &quot;<strong className={emphasis}>we</strong>,&quot; &quot;
          <strong className={emphasis}>us</strong>&quot;). By creating an account or using our Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.
        </p>

        <nav
          aria-label="Table of contents"
          className="mb-14 border-4 border-double border-stone-500/25 bg-white/60 p-5 dark:border-white/15 dark:bg-white/[0.02] sm:p-6"
        >
          <p className={`mb-4 font-mono text-[10px] uppercase tracking-[0.25em] ${proseMuted}`}>Index of sections</p>
          <ol className="columns-1 gap-x-10 gap-y-1 text-[13px] leading-snug sm:columns-2">
            {tocItems.map(([label, href]) => (
              <li key={href} className="break-inside-avoid py-1">
                <a href={href} className={linkIndigo}>
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="space-y-0">
          <Section id="acceptance" title="1. Acceptance of Terms">
            <p>
              By accessing or using VaWeNa at{" "}
              <a href="https://vawena.ai" className={linkIndigo}>
                vawena.ai
              </a>{" "}
              or{" "}
              <a href={APP_URL} className={linkIndigo}>
                app.vawena.ai
              </a>
              , you affirm that you have read, understood, and agree to be bound by these Terms and our{" "}
              <Link to="/privacy-policy" className={linkIndigo}>
                Privacy Policy
              </Link>
              , incorporated by reference.
            </p>
          </Section>

          <Section id="eligibility" title="2. Eligibility">
            <p>
              You must be at least <strong className={emphasis}>18 years of age</strong> and legally capable of entering into binding contracts in your jurisdiction to use VaWeNa. By using the Service, you represent and warrant that you meet these requirements.
            </p>
            <p>
              VaWeNa is currently available to residents of the <strong className={emphasis}>United States</strong>. We do not currently support non-US bank accounts or properties outside the US.
            </p>
          </Section>

          <Section id="account" title="3. Account Registration">
            <p>You agree to provide accurate, current, and complete information during registration and to keep your account information updated.</p>
            <p>
              You are responsible for safeguarding your account credentials. You agree to notify us immediately at <strong className={emphasis}>{EMAIL}</strong> if you suspect unauthorized access to your account. VaWeNa will not be liable for losses arising from unauthorized use of your account.
            </p>
            <p>You may not create more than one account per person, share your account with others, or use the Service on behalf of another person without their consent.</p>
          </Section>

          <Section id="billing" title="4. Subscription & Billing">
            <KeyPoint accent="neutral">
              <p>
                VaWeNa offers paid subscription plans. Billing is handled securely through <strong className={emphasis}>Stripe</strong>. We do not store your payment card details.
              </p>
            </KeyPoint>
            <p>Current subscription plans include:</p>
            <ul className="list-disc space-y-1.5 pl-5 marker:text-stone-500 dark:marker:text-slate-500">
              <li>
                <strong className={emphasis}>WealthScope:</strong> $9.99/month or $99.90/year (17% off)
              </li>
              <li>
                <strong className={emphasis}>PortfolioIQ:</strong> $29.99/month or $299.90/year (17% off)
              </li>
              <li>
                <strong className={emphasis}>VaWeNa Bundle:</strong> $34.99/month or $349.90/year (17% off)
              </li>
            </ul>
            <p>Annual plans are billed in full at the start of the billing period. Monthly plans renew on the same calendar date each month. Prices are in USD.</p>
            <p>
              We reserve the right to change pricing with <strong className={emphasis}>30 days&apos; advance notice</strong> via email or in-app notification. Existing annual subscribers will not be subject to price changes until their renewal date.
            </p>
            <p>
              <strong className={emphasis}>Refunds:</strong> Subscription fees are non-refundable except as required by applicable law or as described in our refund policy. If you cancel during a billing period, you retain access until the end of that period.
            </p>
          </Section>

          <Section id="trial" title="5. Free Trial">
            <p>
              VaWeNa offers a <strong className={emphasis}>14-day free trial</strong> for new subscribers. A valid payment method is required at sign-up; you will <strong className={emphasis}>not be charged</strong> until the trial period ends.
            </p>
            <p>We will send a reminder email at least 3 days before your trial ends so you can cancel if you choose not to continue. If you do not cancel before the end of the trial, your selected plan will automatically renew and your payment method will be charged.</p>
            <p>At the end of the trial period, you will need an active paid plan to continue using the Service. Features may be limited during the trial at our discretion.</p>
            <p>Each email address is eligible for only one free trial. Attempting to circumvent this by creating multiple accounts may result in account termination.</p>
          </Section>

          <Section id="use" title="6. Acceptable Use">
            <p>You agree not to:</p>
            <ul className="list-disc space-y-1.5 pl-5 marker:text-stone-500 dark:marker:text-slate-500">
              <li>Use the Service for any unlawful purpose or in violation of applicable law.</li>
              <li>Input false, misleading, or fraudulent financial information.</li>
              <li>Attempt to gain unauthorized access to any part of the Service or another user&apos;s data.</li>
              <li>Reverse engineer, decompile, or attempt to extract source code from the Service.</li>
              <li>Use automated tools (bots, scrapers) to access the Service without our written permission.</li>
              <li>Transmit malware, viruses, or any code of a destructive nature.</li>
              <li>Resell or sublicense access to the Service without written consent.</li>
            </ul>
            <p>Violation of these terms may result in immediate account suspension or termination without refund.</p>
          </Section>

          <Section id="disclaimer" title="7. Financial Information Disclaimer">
            <KeyPoint accent="amber">
              <p className="font-semibold text-amber-950 dark:text-amber-100">
                Important: VaWeNa does not provide financial, investment, tax, or legal advice.
              </p>
            </KeyPoint>
            <p>
              All content, calculations, metrics (including DSCR, net worth, cash flow, cap rate, buying power), and AI-generated insights provided by VaWeNa are for <strong className={emphasis}>informational and educational purposes only</strong>. They do not constitute financial advice, investment recommendations, tax guidance, or legal counsel.
            </p>
            <p>Financial data displayed may be based on information you provide or retrieved from connected bank accounts. Accuracy depends on the completeness and accuracy of your inputs and third-party data sources. VaWeNa does not guarantee the accuracy, completeness, or timeliness of any financial data or calculations.</p>
            <p>Always consult a licensed financial advisor, tax professional, or attorney before making investment, financing, or real estate decisions.</p>
            <p>Past performance of any real estate investment does not guarantee future results.</p>
          </Section>

          <Section id="third-party" title="8. Third-Party Services">
            <p>VaWeNa integrates with third-party services including Plaid, Flinks, Stripe, and Supabase. Your use of these services is subject to their respective terms and privacy policies.</p>
            <p>We are not responsible for the availability, accuracy, or actions of third-party services. Third-party service outages may temporarily affect VaWeNa functionality.</p>
          </Section>

          <Section id="ip" title="9. Intellectual Property">
            <p>VaWeNa and its content, features, and functionality (including the design, text, graphics, software, and algorithms) are owned by {COMPANY} and are protected by copyright, trademark, and other laws.</p>
            <p>You retain all rights to the financial data you input. You grant VaWeNa a limited, non-exclusive license to use your data solely to operate and improve the Service.</p>
            <p>You may not copy, modify, distribute, or create derivative works of VaWeNa&apos;s proprietary content without our prior written consent.</p>
          </Section>

          <Section id="data" title="10. Data & Privacy">
            <p>
              Your use of the Service is governed by our{" "}
              <Link to="/privacy-policy" className={linkIndigo}>
                Privacy Policy
              </Link>
              . By using VaWeNa, you consent to our data practices as described therein.
            </p>
          </Section>

          <Section id="termination" title="11. Termination">
            <p>You may cancel your subscription and delete your account at any time from account settings or by emailing {EMAIL}.</p>
            <p>We reserve the right to suspend or terminate your account at any time for violation of these Terms, at our sole discretion, with or without notice. Upon termination, your right to use the Service ceases immediately.</p>
            <p>Provisions that by their nature should survive termination (Sections 7, 9, 12, 13) shall survive.</p>
          </Section>

          <Section id="liability" title="12. Limitation of Liability">
            <KeyPoint accent="rose">
              <p>
                To the fullest extent permitted by law, VaWeNa&apos;s total liability to you for any claims arising from use of the Service shall not exceed the amount you paid us in the 12 months preceding the claim.
              </p>
            </KeyPoint>
            <p>VaWeNa shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, loss of investment returns, loss of data, or business interruption, even if advised of the possibility of such damages.</p>
            <p>THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.</p>
          </Section>

          <Section id="disputes" title="13. Dispute Resolution">
            <KeyPoint accent="neutral">
              <p>These Terms are governed by the laws of the State of Delaware, without regard to conflict of law principles.</p>
            </KeyPoint>
            <p>Any disputes shall first be attempted to be resolved informally by contacting us at {EMAIL}. If not resolved within 30 days, disputes shall be resolved by binding arbitration under the rules of the American Arbitration Association (AAA), on an individual basis. You waive any right to participate in a class action lawsuit or class-wide arbitration.</p>
            <p>Notwithstanding the above, either party may seek injunctive or equitable relief in a court of competent jurisdiction for claims involving intellectual property or unauthorized access.</p>
          </Section>

          <Section id="changes" title="14. Changes to Terms">
            <p>
              We may update these Terms from time to time. If we make material changes, we will notify you by email or prominent in-app notice at least <strong className={emphasis}>14 days</strong> before changes take effect. Continued use of the Service after the effective date constitutes acceptance of the updated Terms.
            </p>
          </Section>

          <Section id="contact" title="15. Contact">
            <div className="border border-stone-300 bg-white/70 p-5 dark:border-white/15 dark:bg-white/[0.03] sm:p-6">
              <p className={`${emphasis} mb-3`}>{COMPANY}</p>
              <p>
                Legal inquiries:{" "}
                <a href={`mailto:${EMAIL}`} className={linkIndigo}>
                  {EMAIL}
                </a>
              </p>
              <p>
                General support:{" "}
                <a href="mailto:support@vawena.ai" className={linkIndigo}>
                  support@vawena.ai
                </a>
              </p>
            </div>
          </Section>
        </div>

        <div className={`mt-14 border-t border-stone-400/50 pt-8 dark:border-white/15 flex flex-wrap gap-x-8 gap-y-2 text-sm ${proseMuted}`}>
          <Link to="/privacy-policy" className="hover:text-gray-900 dark:hover:text-white">
            Privacy Policy
          </Link>
          <Link to="/" className="hover:text-gray-900 dark:hover:text-white">
            Back to Home
          </Link>
          <Link to="/contact" className="hover:text-gray-900 dark:hover:text-white">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
